import React, { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot, doc, updateDoc, getDoc, limit } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../../Firebaseconfig';
import PopupCard from '../MainComponents/PopupCard';
import './CompanyBar.css';

export default function CarrierBar() {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [activeTab, setActiveTab]       = useState('ready');
  const [isOnline, setIsOnline]         = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  // Track authenticated user to properly gate Firestore listeners
  const [currentUser, setCurrentUser]   = useState(auth.currentUser);

  /* ── helpers ── */
  const currentUid = () => currentUser?.uid;

  const calculateMinsAgo = (timestamp) => {
    if (!timestamp) return 0;
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return Math.max(0, Math.floor((new Date() - date) / 60000));
  };

  const [carrierProfile, setCarrierProfile] = useState(null);

  /* ── Track auth state ── */
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (!user) {
        setOrders([]);
        setLoading(false);
        setCarrierProfile(null);
      }
    });
    return unsub;
  }, []);

  /* ── Load carrier profile (runs when currentUser changes) ── */
  useEffect(() => {
    if (!currentUser) return;
    const userRef = doc(db, 'users', currentUser.uid);
    getDoc(userRef).then(snap => {
      if (snap.exists()) {
        const data = snap.data();
        setCarrierProfile(data);
        setIsOnline(data.isOnline === true);
      }
    }).catch(console.error);
  }, [currentUser]);

  /* ── Toggle online ── */
  const handleToggleOnline = async () => {
    if (!currentUser || togglingStatus) return;
    const newStatus = !isOnline;
    setTogglingStatus(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), { isOnline: newStatus });
      setIsOnline(newStatus);
      setCarrierProfile(prev => prev ? { ...prev, isOnline: newStatus } : null);
    } catch (err) {
      console.error('Failed to update online status:', err);
    } finally {
      setTogglingStatus(false);
    }
  };

  /* ── Live orders subscription ── */
  /* Two real-time listeners merged client-side.
     orderBy is intentionally omitted from both queries — the composite
     (status + createdAt) and (courierId + createdAt) indexes are not
     guaranteed to exist in every environment. Sorting is handled in JS
     inside updateCombined, so the queries work index-free. */
  useEffect(() => {
    if (!currentUser) return;
    const uid = currentUser.uid;
    let cancelled = false;

    // All orders assigned to this carrier — no orderBy to avoid index requirement
    const qMine = query(
      collection(db, 'orders'),
      where('courierId', '==', uid),
      limit(50)
    );

    let availableData = [];
    let mineData  = [];

    const updateCombined = () => {
      if (cancelled) return;
      const unique = new Map();
      availableData.forEach(o => unique.set(o.id, o));
      mineData.forEach(o  => unique.set(o.id, o));

      const fetched = Array.from(unique.values()).sort((a, b) => {
        const tA = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
        const tB = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
        return tB - tA;
      });

      setOrders(fetched);
      setLoading(false);
    };

    let unsubAvailable = () => {};
    if (isOnline) {
      // All unassigned accepted/ready orders — no orderBy to avoid index requirement
      const qAvailable = query(
        collection(db, 'orders'),
        where('status', 'in', ['accepted', 'ready']),
        limit(50)
      );

      unsubAvailable = onSnapshot(qAvailable, (snapshot) => {
        if (cancelled) return;
        availableData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        updateCombined();
      }, (err) => {
        console.error('Error fetching available orders:', err);
        if (!cancelled) setLoading(false);
      });
    } else {
      availableData = [];
      updateCombined();
    }

    const unsubMine = onSnapshot(qMine, (snapshot) => {
      if (cancelled) return;
      mineData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      updateCombined();
    }, (err) => {
      console.error('Error fetching my orders:', err);
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
      unsubAvailable();
      unsubMine();
    };
  }, [currentUser, isOnline]);

  /* ── Actions ── */

  /** Carrier claims an accepted/ready order → pickingup */
  const handlePickupOrder = async (orderId) => {
    if (!currentUser) return alert('Sign in first.');
    const uid = currentUser.uid;

    // Check if courier already has an order in delivering status
    const deliveringOrders = orders.filter(o => o.status === 'delivering' && o.courierId === uid);
    if (deliveringOrders.length > 0) {
      alert('You cannot take new orders while delivering another order.');
      return;
    }

    // Check if courier already has 3 orders in pickingup status
    const pickingUpOrders = orders.filter(o => o.status === 'pickingup' && o.courierId === uid);
    if (pickingUpOrders.length >= 3) {
      alert('You can claim a maximum of 3 orders at a time.');
      return;
    }

    // Verify restaurant ID match if picking up multiple orders
    const targetOrder = orders.find(o => o.id === orderId);
    if (pickingUpOrders.length > 0 && targetOrder) {
      const matchRestaurant = pickingUpOrders.every(o => o.companyId === targetOrder.companyId);
      if (!matchRestaurant) {
        alert('You can only take multiple orders from the same restaurant.');
        return;
      }
    }

    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'pickingup',
        courierId: currentUser.uid,
        pickedUpAt: new Date(),
      });
      setSelectedOrder(null);
      setActiveTab('pickingup');
    } catch (err) {
      console.error('Error picking up order:', err);
      alert(`Pick-up failed: ${err.message}`);
    }
  };

  /** Carrier leaves the store → delivering */
  const handleDeliverOrder = async (orderId) => {
    if (!currentUser) return alert('Sign in first.');
    const order = orders.find(o => o.id === orderId);
    if (order?.courierId && order.courierId !== currentUser.uid)
      return alert('This order is assigned to another carrier.');
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'delivering',
        deliveryStartedAt: new Date(),
      });
      setSelectedOrder(null);
      setActiveTab('delivering');
    } catch (err) {
      console.error('Error updating to delivering:', err);
      alert(`Error: ${err.message}`);
    }
  };

  /** Mark as delivered */
  const handleCompleteOrder = async (orderId) => {
    if (!currentUser) return alert('Sign in first.');
    const order = orders.find(o => o.id === orderId);
    if (order?.courierId && order.courierId !== currentUser.uid)
      return alert('You can only complete deliveries assigned to you.');
    try {
      await updateDoc(doc(db, 'orders', orderId), {
        status: 'completed',
        completedAt: new Date(),
      });
      setSelectedOrder(null);
    } catch (err) {
      console.error('Error completing delivery:', err);
      alert(`Error: ${err.message}`);
    }
  };

  const handleIgnoreOrder = () => setSelectedOrder(null);

  /* ── Partition orders ── */
  const uid = currentUid();
  const acceptedOrders   = orders.filter(o => o.status === 'accepted' && !o.courierId);
  const readyOrders      = orders.filter(o => o.status === 'ready' && !o.courierId);
  const pickingUpOrders  = orders.filter(o => o.status === 'pickingup' && o.courierId === uid);
  const deliveringOrders = orders.filter(o => o.status === 'delivering' && o.courierId === uid);
  const completedOrders  = orders.filter(o => o.status === 'completed' && o.courierId === uid);

  const displayOrders =
    activeTab === 'accepted'   ? acceptedOrders :
    activeTab === 'ready'      ? readyOrders :
    activeTab === 'pickingup'  ? pickingUpOrders :
    activeTab === 'delivering' ? deliveringOrders :
    completedOrders;

  const avgDeliveryTime = (() => {
    const times = completedOrders
      .map(o => {
        if (!o.completedAt || !o.pickedUpAt) return null;
        const start = o.pickedUpAt.toDate ? o.pickedUpAt.toDate() : new Date(o.pickedUpAt);
        const end = o.completedAt.toDate ? o.completedAt.toDate() : new Date(o.completedAt);
        return (end - start) / 60000;
      })
      .filter(t => t !== null && t > 0);
    
    if (times.length === 0) return 22.5;
    return Math.round(times.reduce((sum, t) => sum + t, 0) / times.length * 10) / 10;
  })();

  const dailyEarnings = completedOrders
    .filter(o => {
      const date = o.completedAt?.toDate ? o.completedAt.toDate() : new Date(o.completedAt);
      return new Date().toDateString() === date.toDateString();
    })
    .reduce((sum, o) => sum + Number(o.total || 0), 0);

  const rating = Number(carrierProfile?.rating || 4.9);
  const stars = '★'.repeat(Math.round(rating)) + '☆'.repeat(5 - Math.round(rating));

  /* ── Render one order card ── */
  const renderCard = (order) => {
    const minsAgo = calculateMinsAgo(order.createdAt);
    const isUrgent = minsAgo > 10;

    return (
      <div
        key={order.id}
        className={`cb-order-card ${isUrgent ? 'cb-order-card--urgent' : ''}`}
        onClick={() => setSelectedOrder(order)}
        style={{ cursor: 'pointer' }}
      >
        <div className={`cb-card-accent ${isUrgent ? 'cb-card-accent--urgent' : ''}`} />

        <div className={`cb-avatar ${isUrgent ? 'cb-avatar--urgent' : ''}`}>
          {(order.customerName || 'U')[0].toUpperCase()}
        </div>

        <div className="cb-customer-col">
          <div className="cb-col-lbl">CUSTOMER</div>
          <div className="cb-customer-name">{order.customerName || 'Unknown Customer'}</div>
          <div className="cb-order-id">#{order.orderId || order.id.slice(0, 8).toUpperCase()}</div>
        </div>

        <div className="cb-col-divider" />

        <div className="cb-items-col">
          <div className="cb-col-lbl">ORDER ITEMS</div>
          <div className="cb-items-text">{order.itemsText || '1x Item'}</div>
          <div className="cb-badges">
            {(order.tags || []).map((tag, i) => (
              <span key={i} className={`cb-badge ${tag.includes('SPICY') ? 'orange-badge' : ''}`}>{tag}</span>
            ))}
          </div>
        </div>

        <div className="cb-col-divider" />

        {/* Address column */}
        <div className="cb-time-col" style={{ width: 160 }}>
          <div className="cb-col-lbl">DELIVERY ADDRESS</div>
          <div style={{ fontSize: 12, color: '#c0c5d0', lineHeight: 1.4, marginTop: 2 }}>
            {order.address || 'No address provided'}
          </div>
        </div>

        <div className="cb-col-divider" />

        <div className="cb-total-col">
          <div className="cb-col-lbl">TOTAL</div>
          <div className="cb-total-val">${Number(order.total || 0).toFixed(2)}</div>
          <div className="cb-payment">{order.paymentMethod || 'Card'}</div>
        </div>

        <div className="cb-actions-col">
          {/* ACCEPTED OR READY — show Pick Up */}
          {(order.status === 'accepted' || order.status === 'ready') && (
            <>
              <button
                className="cb-btn-accept"
                onClick={(e) => { e.stopPropagation(); handlePickupOrder(order.id); }}
              >
                🛍 Pick Up
              </button>
              <button
                className="cb-btn-reject"
                onClick={(e) => { e.stopPropagation(); handleIgnoreOrder(); }}
              >
                ✕ Ignore
              </button>
            </>
          )}

          {/* PICKING UP — show Deliver */}
          {order.status === 'pickingup' && (
            <>
              <button
                className="cb-btn-accept"
                style={{ background: 'linear-gradient(135deg,#a855f7,#7c3aed)' }}
                onClick={(e) => { e.stopPropagation(); handleDeliverOrder(order.id); }}
              >
                🚴 Deliver
              </button>
              <button
                className="cb-btn-reject"
                onClick={(e) => { e.stopPropagation(); handleIgnoreOrder(); }}
              >
                ✕ Cancel
              </button>
            </>
          )}

          {/* DELIVERING — show Complete */}
          {order.status === 'delivering' && (
            <>
              <button
                className="cb-btn-accept"
                style={{ background: '#22c55e' }}
                onClick={(e) => { e.stopPropagation(); handleCompleteOrder(order.id); }}
              >
                ✓ Complete
              </button>
            </>
          )}

          {/* COMPLETED */}
          {order.status === 'completed' && (
            <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ● Delivered
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="company-dashboard">

      {/* ── Active Delivery Banners ── */}
      {(() => {
        const myActiveOrders = [...orders.filter(o => o.status === 'pickingup' && o.courierId === currentUid()), ...orders.filter(o => o.status === 'delivering' && o.courierId === currentUid())];
        return myActiveOrders.map(activeOrder => (
          <div key={activeOrder.id} style={{
            background: 'linear-gradient(135deg, rgba(168,85,247,0.15), rgba(59,130,246,0.1))',
            border: '1px solid rgba(168,85,247,0.35)',
            borderRadius: 14,
            padding: '18px 24px',
            marginBottom: 24,
            display: 'flex',
            alignItems: 'center',
            gap: 20,
            flexWrap: 'wrap',
          }}>
            {/* Animated pulse dot */}
            <div style={{ position: 'relative', flexShrink: 0 }}>
              <div style={{
                width: 48, height: 48, borderRadius: '50%',
                background: 'rgba(168,85,247,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 22,
                animation: 'none',
              }}>
                {activeOrder.status === 'delivering' ? '🚴' : '🛍'}
              </div>
              <span style={{
                position: 'absolute', top: 0, right: 0,
                width: 12, height: 12, borderRadius: '50%',
                background: activeOrder.status === 'delivering' ? '#a855f7' : '#f97316',
                boxShadow: `0 0 10px ${activeOrder.status === 'delivering' ? '#a855f7' : '#f97316'}`,
                border: '2px solid #0d0f1e',
              }} />
            </div>

            {/* Status text */}
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: activeOrder.status === 'delivering' ? '#a855f7' : '#f97316', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>
                {activeOrder.status === 'delivering' ? '● On the Way' : '● Picking Up'}
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#f1f3fb', marginBottom: 2 }}>
                {activeOrder.customerName || 'Customer'}
              </div>
              <div style={{ fontSize: 12, color: '#94a3b8' }}>
                Order #{activeOrder.orderId || activeOrder.id.slice(0,8).toUpperCase()} · {activeOrder.itemsText || '1x Item'}
              </div>
            </div>

            {/* Address */}
            <div style={{ minWidth: 200, padding: '10px 16px', background: 'rgba(255,255,255,0.04)', borderRadius: 10, border: '1px solid rgba(255,255,255,0.07)' }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: '#4b5563', letterSpacing: '0.08em', marginBottom: 6, textTransform: 'uppercase' }}>
                📍 Delivery Address
              </div>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0', lineHeight: 1.4 }}>
                {activeOrder.address || 'No address provided'}
              </div>
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120 }}>
              {activeOrder.status === 'pickingup' && (
                <button
                  style={{
                    background: 'linear-gradient(135deg,#a855f7,#7c3aed)',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 700, fontSize: 13,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(168,85,247,0.35)',
                    letterSpacing: '0.02em',
                  }}
                  onClick={() => handleDeliverOrder(activeOrder.id)}
                >
                  🚴 Start Delivery
                </button>
              )}
              {activeOrder.status === 'delivering' && (
                <button
                  style={{
                    background: 'linear-gradient(135deg,#22c55e,#16a34a)',
                    color: '#fff', border: 'none', borderRadius: 8,
                    padding: '10px 18px', fontWeight: 700, fontSize: 13,
                    cursor: 'pointer', boxShadow: '0 4px 14px rgba(34,197,94,0.35)',
                    letterSpacing: '0.02em',
                  }}
                  onClick={() => handleCompleteOrder(activeOrder.id)}
                >
                  ✓ Mark Delivered
                </button>
              )}
            </div>

            {/* Simulated map grid */}
            <div style={{
              position: 'relative',
              width: 110, height: 80,
              borderRadius: 10, overflow: 'hidden',
              border: '1px solid rgba(168,85,247,0.25)',
              flexShrink: 0,
            }}>
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(168,85,247,0.1) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(168,85,247,0.1) 1px, transparent 1px)
                `,
                backgroundSize: '18px 18px',
                background: '#0d0f1e',
              }} />
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `
                  linear-gradient(rgba(168,85,247,0.08) 1px, transparent 1px),
                  linear-gradient(90deg, rgba(168,85,247,0.08) 1px, transparent 1px)
                `,
                backgroundSize: '18px 18px',
              }} />
              {/* Destination pin */}
              <div style={{
                position: 'absolute', top: '30%', left: '60%',
                width: 10, height: 10, borderRadius: '50%',
                background: '#22c55e', boxShadow: '0 0 10px #22c55e',
              }} />
              {/* Carrier dot */}
              <div style={{
                position: 'absolute',
                top: activeOrder.status === 'pickingup' ? '60%' : '50%',
                left: activeOrder.status === 'pickingup' ? '30%' : '45%',
                width: 8, height: 8, borderRadius: '50%',
                background: '#a855f7', boxShadow: '0 0 8px #a855f7',
              }} />
              {/* Dotted path line */}
              <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} viewBox="0 0 110 80">
                <line
                  x1={activeOrder.status === 'pickingup' ? '33' : '50'}
                  y1={activeOrder.status === 'pickingup' ? '48' : '40'}
                  x2="66" y2="24"
                  stroke="#a855f7" strokeWidth="1.5" strokeDasharray="4 3" strokeOpacity="0.5"
                />
              </svg>
              <div style={{ position: 'absolute', bottom: 4, left: 0, right: 0, textAlign: 'center', fontSize: 8, color: '#6b7280', fontWeight: 600 }}>
                {activeOrder.status === 'delivering' ? 'EN ROUTE' : 'AT STORE'}
              </div>
            </div>
          </div>
        ));
      })()}

      {/* ── Header ── */}
      <div className="cb-header-row">
        <div>
          <h1 className="cb-title">Available Deliveries</h1>
          <p className="cb-subtitle" style={{ color: isOnline ? '#22c55e' : '#6b7280' }}>
            {isOnline ? '● Online — You are visible to customers' : '○ Offline — Go online to receive orders'}
          </p>
        </div>
        <div className="cb-header-actions">
          {/* Online toggle */}
          <div
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: togglingStatus ? 'not-allowed' : 'pointer' }}
            onClick={handleToggleOnline}
            title={isOnline ? 'Go Offline' : 'Go Online'}
          >
            <span style={{ fontSize: 12.5, fontWeight: 700, color: isOnline ? '#22c55e' : '#6b7280', letterSpacing: '0.04em', userSelect: 'none' }}>
              {isOnline ? 'ONLINE' : 'OFFLINE'}
            </span>
            <div style={{
              width: 46, height: 26, borderRadius: 999,
              background: isOnline ? 'rgba(34,197,94,0.25)' : 'rgba(107,114,128,0.2)',
              border: `1.5px solid ${isOnline ? '#22c55e' : '#374151'}`,
              position: 'relative',
              transition: 'background 0.25s, border-color 0.25s',
              opacity: togglingStatus ? 0.6 : 1,
            }}>
              <div style={{
                position: 'absolute',
                top: 3, left: isOnline ? 23 : 3,
                width: 18, height: 18, borderRadius: '50%',
                background: isOnline ? '#22c55e' : '#6b7280',
                boxShadow: isOnline ? '0 0 8px rgba(34,197,94,0.7)' : 'none',
                transition: 'left 0.25s, background 0.25s, box-shadow 0.25s',
              }} />
            </div>
          </div>
          <button className="cb-btn cb-btn-dark">Map Display</button>
        </div>
      </div>

      {/* ── Tabs ── */}
      <div className="cb-tabs-row">
        <div className="cb-tabs">
          <div
            className={`cb-tab ${activeTab === 'accepted' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('accepted')}
          >
            Accepted ({acceptedOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'ready' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('ready')}
          >
            Ready ({readyOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'pickingup' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('pickingup')}
            style={pickingUpOrders.length > 0 ? { color: '#f97316' } : {}}
          >
            Picking Up ({pickingUpOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'delivering' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('delivering')}
            style={deliveringOrders.length > 0 ? { color: '#a855f7' } : {}}
          >
            Delivering ({deliveringOrders.length})
          </div>
          <div
            className={`cb-tab ${activeTab === 'completed' ? 'cb-tab-active' : ''}`}
            onClick={() => setActiveTab('completed')}
          >
            Completed ({completedOrders.length})
          </div>
        </div>
        <div className="cb-live-sync">
          <span className="sync-dot" /> Live Syncing
        </div>
      </div>

      {/* ── Order list ── */}
      <div className="cb-orders-list">
        {loading ? (
          <div style={{ color: '#8a8f9e', padding: '20px' }}>Loading orders...</div>
        ) : displayOrders.length === 0 ? (
          <div style={{ color: '#8a8f9e', padding: '20px' }}>
            {activeTab === 'accepted'
              ? 'No accepted orders available.'
              : activeTab === 'ready'
              ? 'No orders ready for pick-up.'
              : activeTab === 'pickingup'
              ? 'You have not picked up any orders yet.'
              : activeTab === 'delivering'
              ? 'No active deliveries.'
              : 'No completed deliveries yet.'}
          </div>
        ) : (
          displayOrders.map(order => renderCard(order))
        )}
      </div>

      {/* ── Stats ── */}
      <div className="cb-stats-row">
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">AVG. DELIVERY TIME</div>
          <div className="cb-stat-val">{avgDeliveryTime}m</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">DAILY EARNINGS</div>
          <div className="cb-stat-val">${dailyEarnings.toFixed(2)}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">COMPLETED DELIVERIES</div>
          <div className="cb-stat-val">{completedOrders.length}</div>
        </div>
        <div className="cb-stat-card">
          <div className="cb-stat-lbl">RIDER RATING</div>
          <div className="cb-stat-val">
            {rating.toFixed(1)} <span className="cb-stars">{stars}</span>
          </div>
        </div>
      </div>

      {/* ── Popup detail card ── */}
      {selectedOrder && (
        <div className="cb-modal-overlay" onClick={() => setSelectedOrder(null)}>
          <div onClick={(e) => e.stopPropagation()}>
            <PopupCard
              category={selectedOrder.tags?.length > 0 ? selectedOrder.tags[0] : 'ORDER DETAILS'}
              title={selectedOrder.itemsText}
              customerName={selectedOrder.customerName}
              address={selectedOrder.address}
              orderId={selectedOrder.orderId || selectedOrder.id.slice(0, 8).toUpperCase()}
              orderTime={
                selectedOrder.createdAt
                  ? new Date(selectedOrder.createdAt.toDate ? selectedOrder.createdAt.toDate() : selectedOrder.createdAt)
                      .toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                  : new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
              }
              isNew={selectedOrder.status === 'accepted' || selectedOrder.status === 'ready'}
              readyInMins={selectedOrder.readyInMins}
              onRemove={handleIgnoreOrder}
              onAccept={
                (selectedOrder.status === 'accepted' || selectedOrder.status === 'ready')
                  ? () => handlePickupOrder(selectedOrder.id)
                  : selectedOrder.status === 'pickingup'
                  ? () => handleDeliverOrder(selectedOrder.id)
                  : selectedOrder.status === 'delivering'
                  ? () => handleCompleteOrder(selectedOrder.id)
                  : handleIgnoreOrder
              }
            />
          </div>
        </div>
      )}
    </div>
  );
}
